import { useState, memo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Tooltip,
  Chip,
} from '@mui/material';
import { Archive as ArchiveIcon } from '@mui/icons-material';
import type { Board, Hit, CustomPayoutCategory, HighlightedCellsWithQuarter, PendingScoreUpdate } from '../../types';
import { hitsApi } from '../../api';
import { HitCard, getSquareOwnerName } from './HitCard';
import { PendingScoreCard } from './PendingScoreCard';
import { ConfirmDialog } from '../shared/ConfirmDialog';

// Re-export for backward compatibility
// eslint-disable-next-line react-refresh/only-export-components
export { getSquareOwnerName };

// Get quarter labels based on sport type
// eslint-disable-next-line react-refresh/only-export-components
export const getQuarterLabels = (quarters: number, otEnabled: boolean): { quarter: number; label: string; shortLabel: string }[] => {
  if (quarters === 2) {
    return [
      { quarter: 1, label: '1st Half', shortLabel: 'H1' },
      { quarter: 2, label: '2nd Half / Final', shortLabel: 'H2' },
    ];
  }
  if (quarters === 3) {
    return [
      { quarter: 1, label: '1st Period', shortLabel: 'P1' },
      { quarter: 2, label: '2nd Period', shortLabel: 'P2' },
      { quarter: 3, label: '3rd Period / Final', shortLabel: 'P3' },
    ];
  }
  const baseQuarters = [
    { quarter: 1, label: 'Q1', shortLabel: 'Q1' },
    { quarter: 2, label: 'Q2 / Halftime', shortLabel: 'Q2' },
    { quarter: 3, label: 'Q3', shortLabel: 'Q3' },
    { quarter: 4, label: otEnabled ? 'Q4 (End of Reg)' : 'Q4 / Final', shortLabel: 'Q4' },
  ];
  if (otEnabled) {
    baseQuarters.push({ quarter: 0, label: 'Final (OT)', shortLabel: 'F' });
  }
  return baseQuarters;
};

export interface HitsPanelProps {
  board: Board;
  hits: Hit[];
  isManager: boolean;
  boardId: string;
  currentUserId?: string;
  onRecordHit?: () => void;
  onHitHover?: (data: HighlightedCellsWithQuarter | null) => void;
  onComplete?: () => void;
  showCustomPayouts?: boolean;
  terseMode?: boolean;
  textColorMode?: 'light' | 'dark';
  /** Number of unclaimed squares - used to show confirmation before first score entry */
  unclaimedSquaresCount?: number;
  /** Pending score updates from the live score worker */
  pendingScoreUpdates?: PendingScoreUpdate[];
  /** Whether this board is a live game board */
  isLiveGame?: boolean;
}

// Build subheader for quarter no-hit state
const QuarterSubheader = ({ canRecord }: { canRecord: boolean }) => (
  <Typography variant="caption" color="text.secondary">
    {canRecord ? 'Not recorded' : 'Record previous first'}
  </Typography>
);

// Build subheader for custom category no-hit state
const CustomCategorySubheader = ({ category }: { category: CustomPayoutCategory }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
    <Typography variant="body2" color="text.secondary">
      ${category.payoutAmount.toFixed(2)} payout
      {category.wrongWayEnabled && ` x2 = $${(category.payoutAmount * 2).toFixed(2)}`}
    </Typography>
    {category.wrongWayEnabled && (
      <Chip label="2-WAY" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'grey.300' }} />
    )}
  </Box>
);

export const HitsPanel = memo<HitsPanelProps>(({
  board,
  hits,
  isManager,
  boardId,
  currentUserId,
  onRecordHit,
  onHitHover,
  onComplete,
  showCustomPayouts = false,
  terseMode = false,
  textColorMode,
  unclaimedSquaresCount = 0,
  pendingScoreUpdates,
  isLiveGame = false,
}) => {
  const [editingQuarter, setEditingQuarter] = useState<number | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Track if user has confirmed the unclaimed squares warning for this session
  const [unclaimedWarningConfirmed, setUnclaimedWarningConfirmed] = useState(false);
  // Confirmation dialog states
  const [unclaimedConfirmOpen, setUnclaimedConfirmOpen] = useState(false);
  const [pendingEditQuarter, setPendingEditQuarter] = useState<number | null>(null);
  const [pendingEditCategoryId, setPendingEditCategoryId] = useState<string | null>(null);
  const [deleteScoreConfirm, setDeleteScoreConfirm] = useState<{ hitId: string; quarterIndex: number } | null>(null);
  const [deleteStatConfirm, setDeleteStatConfirm] = useState<string | null>(null);

  const otEnabled = board.payoutConfig?.overtimeConfig?.enabled ?? false;
  const quarterLabels = getQuarterLabels(board.quarters || 4, otEnabled);
  const customCategories = showCustomPayouts ? (board.payoutConfig?.customCategories || []) : [];
  const sortedCategories = [...customCategories].sort((a, b) => a.displayOrder - b.displayOrder);

  // Always use "squares" terminology regardless of cell mode
  const unitLabelLower = 'squares';

  const canEdit = isManager && board.status !== 'completed';

  // Check if current user is a winner of any of the hits (main, wrong-way, +N, +N wrong-way, or neighbor)
  const isUserWinnerOfHits = (hit?: Hit, wrongWayHit?: Hit, plusNHit?: Hit, plusNWrongWayHit?: Hit): boolean => {
    if (!currentUserId) return false;
    const checkHit = (h?: Hit) => h?.winningSquare?.owner?.id === currentUserId;
    const checkNeighbor = hit?.neighborPayouts?.some(p => p.user?.id === currentUserId) ?? false;
    return checkHit(hit) || checkHit(wrongWayHit) || checkHit(plusNHit) || checkHit(plusNWrongWayHit) || checkNeighbor;
  };

  // Check if we need to show unclaimed squares warning before recording first score
  // Note: This must be defined before early returns to satisfy React Hooks rules
  const needsUnclaimedWarning = useCallback((): boolean => {
    const hasNoHits = hits.filter(h => !h.isWrongWay && !h.isPlusN).length === 0;
    return hasNoHits && unclaimedSquaresCount > 0 && !unclaimedWarningConfirmed;
  }, [hits, unclaimedSquaresCount, unclaimedWarningConfirmed]);

  // Draft boards cannot record scores
  if (board.status === 'draft') {
    return (
      <Box>
        <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.300', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Publish the board to start recording game scores
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Live boards with non-randomized numbers need to randomize first
  if (board.status === 'live' && !board.numbersRevealed) {
    return (
      <Box>
        <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px dashed', borderColor: 'grey.300', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Score recording will be available after numbers are randomized
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Get main hit for quarter (not wrong-way or +N)
  const getHitForQuarter = (quarter: number): Hit | undefined => {
    return hits.find((h) => h.quarter === quarter && !h.isWrongWay && !h.isPlusN);
  };

  // Get main hit for custom category
  const getHitForCategory = (categoryId: string): Hit | undefined => {
    return hits.find((h) => h.categoryId === categoryId && !h.isWrongWay && !h.isPlusN);
  };

  // Get wrong-way hit for a main hit (exclude +N wrong way which has both flags)
  const getWrongWayHitForHit = (mainHitId: string): Hit | undefined => {
    return hits.find((h) => h.parentHitId === mainHitId && h.isWrongWay && !h.isPlusN);
  };

  // Get +N hit for a main hit (exclude +N wrong way which has both flags)
  const getPlusNHitForHit = (mainHitId: string): Hit | undefined => {
    return hits.find((h) => h.parentHitId === mainHitId && h.isPlusN && !h.isWrongWay);
  };

  // Get +N wrong-way hit for a main hit (has both isPlusN and isWrongWay)
  const getPlusNWrongWayHitForHit = (mainHitId: string): Hit | undefined => {
    return hits.find((h) => h.parentHitId === mainHitId && h.isPlusN && h.isWrongWay);
  };

  const canRecordQuarter = (quarterIndex: number): boolean => {
    if (quarterIndex === 0) return true;
    for (let i = 0; i < quarterIndex; i++) {
      const prevQuarter = quarterLabels[i].quarter;
      if (!getHitForQuarter(prevQuarter)) {
        return false;
      }
    }
    return true;
  };

  const canDeleteQuarter = (quarterIndex: number): boolean => {
    for (let i = quarterIndex + 1; i < quarterLabels.length; i++) {
      const nextQuarter = quarterLabels[i].quarter;
      if (getHitForQuarter(nextQuarter)) {
        return false;
      }
    }
    return true;
  };

  const getPreviousQuarterScores = (quarterIndex: number): { home: number; away: number } => {
    if (quarterIndex === 0) return { home: 0, away: 0 };
    const prevQuarter = quarterLabels[quarterIndex - 1].quarter;
    const prevHit = getHitForQuarter(prevQuarter);
    return prevHit ? { home: prevHit.homeScore, away: prevHit.awayScore } : { home: 0, away: 0 };
  };

  // Wrapper to check warning before starting to edit
  const handleStartEditingQuarter = (quarter: number) => {
    if (needsUnclaimedWarning()) {
      setPendingEditQuarter(quarter);
      setUnclaimedConfirmOpen(true);
    } else {
      setEditingQuarter(quarter);
    }
  };

  const handleStartEditingCategory = (categoryId: string) => {
    if (needsUnclaimedWarning()) {
      setPendingEditCategoryId(categoryId);
      setUnclaimedConfirmOpen(true);
    } else {
      setEditingCategoryId(categoryId);
    }
  };

  const handleUnclaimedConfirm = () => {
    setUnclaimedWarningConfirmed(true);
    setUnclaimedConfirmOpen(false);
    if (pendingEditQuarter !== null) {
      setEditingQuarter(pendingEditQuarter);
      setPendingEditQuarter(null);
    }
    if (pendingEditCategoryId !== null) {
      setEditingCategoryId(pendingEditCategoryId);
      setPendingEditCategoryId(null);
    }
  };

  const handleUnclaimedCancel = () => {
    setUnclaimedConfirmOpen(false);
    setPendingEditQuarter(null);
    setPendingEditCategoryId(null);
  };

  const handleSaveScore = async (quarter: number, quarterLabel: string, homeScore: number, awayScore: number) => {
    setError(null);
    try {
      const existingHit = getHitForQuarter(quarter);

      if (existingHit) {
        await hitsApi.update(boardId, existingHit.id, {
          homeScore,
          awayScore,
          description: quarterLabel,
        });
      } else {
        await hitsApi.record(boardId, {
          quarter,
          homeScore,
          awayScore,
          description: quarterLabel,
        });
      }
      setEditingQuarter(null);
      onRecordHit?.();
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Failed to save score';
      setError(message);
    }
  };

  const handleSaveCustomScore = async (categoryId: string, categoryName: string, homeValue: number, awayValue: number) => {
    setError(null);
    try {
      const existingHit = getHitForCategory(categoryId);

      if (existingHit) {
        await hitsApi.update(boardId, existingHit.id, {
          homeScore: homeValue,
          awayScore: awayValue,
          description: categoryName,
        });
      } else {
        await hitsApi.recordCustomHit(boardId, {
          categoryId,
          homeValue,
          awayValue,
          description: categoryName,
        });
      }
      setEditingCategoryId(null);
      onRecordHit?.();
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Failed to save stat';
      setError(message);
    }
  };

  const handleDeleteHit = (hitId: string, quarterIndex: number) => {
    if (!canDeleteQuarter(quarterIndex)) {
      setError('Cannot delete this score - delete later scores first');
      return;
    }
    setDeleteScoreConfirm({ hitId, quarterIndex });
  };

  const doDeleteScore = async () => {
    if (!deleteScoreConfirm) return;
    try {
      await hitsApi.delete(boardId, deleteScoreConfirm.hitId);
      onRecordHit?.();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to delete score');
    } finally {
      setDeleteScoreConfirm(null);
    }
  };

  const handleDeleteCustomHit = (hitId: string) => {
    setDeleteStatConfirm(hitId);
  };

  const doDeleteStat = async () => {
    if (!deleteStatConfirm) return;
    try {
      await hitsApi.delete(boardId, deleteStatConfirm);
      onRecordHit?.();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to delete stat');
    } finally {
      setDeleteStatConfirm(null);
    }
  };

  const allQuartersRecorded = quarterLabels.every(({ quarter }) => getHitForQuarter(quarter));

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLiveGame && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Scores are recorded automatically for this live game. Approve each quarter&apos;s score below when it appears.
        </Alert>
      )}

      {/* Grid for quarters: 1-column for quarter mode (different numbers each quarter), 2-column otherwise */}
      <Box sx={{ display: 'grid', gridTemplateColumns: board.numberMode === 'quarter' ? '1fr' : 'repeat(2, 1fr)', gap: 1 }}>
        {quarterLabels.map(({ quarter, label }, index) => {
          const hit = getHitForQuarter(quarter);
          const wrongWayHit = hit ? getWrongWayHitForHit(hit.id) : undefined;
          const plusNHit = hit ? getPlusNHitForHit(hit.id) : undefined;
          const plusNWrongWayHit = hit ? getPlusNWrongWayHitForHit(hit.id) : undefined;
          const prevScores = getPreviousQuarterScores(index);
          const canRecord = canRecordQuarter(index);

          // Check for a pending score update for this quarter
          const pendingUpdate = isLiveGame
            ? pendingScoreUpdates?.find(p => p.quarter === quarter && (p.status === 'pending' || p.status === 'edited'))
            : undefined;

          // Show PendingScoreCard if there's a pending update and no hit recorded yet
          if (isLiveGame && pendingUpdate && !hit) {
            return (
              <PendingScoreCard
                key={quarter}
                pending={pendingUpdate}
                boardId={boardId}
                title={label}
                homeTeam={board.homeTeam}
                awayTeam={board.awayTeam}
                isManager={isManager}
                onComplete={() => onRecordHit?.()}
              />
            );
          }

          return (
            <HitCard
              key={quarter}
              title={label}
              hit={hit}
              wrongWayHit={wrongWayHit}
              plusNHit={plusNHit}
              plusNWrongWayHit={plusNWrongWayHit}
              quarter={quarter}
              neighborConfig={board.payoutConfig?.neighborPayouts}
              boxNeighborConfig={board.payoutConfig?.boxNeighborPayouts}
              isEditing={editingQuarter === quarter}
              isCurrentUserWinner={isUserWinnerOfHits(hit, wrongWayHit, plusNHit, plusNWrongWayHit)}
              canEdit={isLiveGame ? false : canEdit}
              canRecord={isLiveGame ? false : canRecord}
              showDelete={isLiveGame ? false : canDeleteQuarter(index)}
              homeTeam={board.homeTeam}
              awayTeam={board.awayTeam}
              defaultHomeScore={prevScores.home}
              defaultAwayScore={prevScores.away}
              terseMode={terseMode}
              subheaderContent={!hit ? (
                isLiveGame
                  ? <Typography variant="caption" color="text.secondary">Waiting for live score...</Typography>
                  : <QuarterSubheader canRecord={canRecord} />
              ) : undefined}
              onStartEditing={isLiveGame ? () => {} : () => handleStartEditingQuarter(quarter)}
              onCancelEditing={() => setEditingQuarter(null)}
              onSaveScore={(homeScore, awayScore) => handleSaveScore(quarter, label, homeScore, awayScore)}
              onDeleteHit={hit && !isLiveGame ? () => handleDeleteHit(hit.id, index) : undefined}
              onHitHover={onHitHover || (() => {})}
            />
          );
        })}
      </Box>

      {/* Custom payouts section - matches quarter grid layout */}
      {showCustomPayouts && sortedCategories.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
            Custom Payouts
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: board.numberMode === 'quarter' ? '1fr' : 'repeat(2, 1fr)', gap: 1 }}>
            {sortedCategories.map((category) => {
              const hit = getHitForCategory(category.id);
              const wrongWayHit = hit ? getWrongWayHitForHit(hit.id) : undefined;
              const plusNHit = hit ? getPlusNHitForHit(hit.id) : undefined;
              const plusNWrongWayHit = hit ? getPlusNWrongWayHitForHit(hit.id) : undefined;

              return (
                <HitCard
                  key={category.id}
                  title={category.name}
                  titleTooltip={category.name}
                  hit={hit}
                  wrongWayHit={wrongWayHit}
                  plusNHit={plusNHit}
                  plusNWrongWayHit={plusNWrongWayHit}
                  neighborConfig={board.payoutConfig?.neighborPayouts}
                  boxNeighborConfig={board.payoutConfig?.boxNeighborPayouts}
                  isEditing={editingCategoryId === category.id}
                  isCurrentUserWinner={isUserWinnerOfHits(hit, wrongWayHit, plusNHit, plusNWrongWayHit)}
                  canEdit={canEdit}
                  showDelete={true}
                  homeTeam={board.homeTeam}
                  awayTeam={board.awayTeam}
                  terseMode={terseMode}
                  subheaderContent={!hit ? <CustomCategorySubheader category={category} /> : undefined}
                  onStartEditing={() => handleStartEditingCategory(category.id)}
                  onCancelEditing={() => setEditingCategoryId(null)}
                  onSaveScore={(homeValue, awayValue) => handleSaveCustomScore(category.id, category.name, homeValue, awayValue)}
                  onDeleteHit={hit ? () => handleDeleteCustomHit(hit.id) : undefined}
                  onHitHover={onHitHover || (() => {})}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* Complete Board button - at the bottom */}
      {isManager && onComplete && (board.status === 'live' || board.status === 'locked') && (
        <Box sx={{ mt: 2 }}>
          <Tooltip title={!allQuartersRecorded ? 'Record all scores before completing the board' : ''}>
            <span>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                startIcon={<ArchiveIcon />}
                onClick={onComplete}
                disabled={!allQuartersRecorded}
                data-testid="complete-button"
                sx={{
                  fontWeight: 'bold',
                  ...(textColorMode === 'light' && {
                    bgcolor: '#2e7d32',
                    color: '#ffffff',
                    '&:hover': {
                      bgcolor: '#1b5e20',
                    },
                  }),
                }}
              >
                Complete Board
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Unclaimed Squares Warning Dialog */}
      <ConfirmDialog
        open={unclaimedConfirmOpen}
        title={`Record score with unclaimed ${unitLabelLower}?`}
        icon="warning"
        message={`There are ${unclaimedSquaresCount} ${unitLabelLower} that have not been claimed.

Recording a score will start the game - unclaimed ${unitLabelLower} cannot win payouts.

Are you sure you want to continue?`}
        confirmLabel="Continue"
        confirmColor="warning"
        onConfirm={handleUnclaimedConfirm}
        onCancel={handleUnclaimedCancel}
      />

      {/* Delete Score Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteScoreConfirm}
        title="Delete Score"
        icon="warning"
        message="Delete this score record?"
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={doDeleteScore}
        onCancel={() => setDeleteScoreConfirm(null)}
      />

      {/* Delete Stat Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteStatConfirm}
        title="Delete Stat"
        icon="warning"
        message="Delete this stat record?"
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={doDeleteStat}
        onCancel={() => setDeleteStatConfirm(null)}
      />
    </Box>
  );
});

HitsPanel.displayName = 'HitsPanel';

export default HitsPanel;
